import React, {
  useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback,
} from 'react';
import { classifyFormat, isMpegTs, looksLikeMp4 } from './lib/formats';
import { mseVideoOnly } from './lib/mseVideoOnly';
import { attachMpegts } from './lib/mpegtsPlayer';

/* Drop-in replacement for ReactPlayer's `.file` mode, wrapping a raw
   <video> element. Exposes the same imperative API ({getCurrentTime,
   getDuration, seekTo, getInternalPlayer}) so existing controls and
   parent code don't need to change.

   What this adds on top of ReactPlayer:
   - probe-driven preload strategy ("metadata" vs "auto")
   - playhead-aware buffer-ahead metric (not just furthest range end)
   - spinner debounce — 400 ms before flagging buffering
   - stall watchdog with 3-tier recovery (soft seek → hard reload → give up)
   - mp4box video-only fallback on decode error code 3 (corrupt audio)
   - mpegts.js wrapper for .ts/.m2ts/.mts sources
   - clear error for known-unsupported formats (.mpg, .avi, .wmv...)
   - smart autoplay: try unmuted, fall back to muted on NotAllowedError
   - decode-quality watchdog (dropped-frame %) → onDecodeWarning prop
   - clean reset of all in-flight async work on URL change / unmount
*/

const NativeVideoPlayer = forwardRef(({
  url,
  probe = null,
  playing = true,
  muted = false,
  volume = 1,
  playbackRate = 1,
  autoPlay = false,
  className,
  width,
  height,
  style,
  onProgress,
  onBuffer,
  onBufferEnd,
  onReady,
  onStart,
  onError,
  onPlay,
  onEnded,
  onDecodeWarning,
  progressInterval = 100,
}, ref) => {
  const videoRef = useRef(null);

  // Pipeline state — which decoder is currently bound to <video>
  const tsMode = isMpegTs(url, probe);
  const tsPlayerRef = useRef(null);
  const [fallbackState, setFallbackState] = useState('idle'); // idle | recovering | active | failed
  const fallbackAbortRef = useRef(null);
  const fallbackAttemptedRef = useRef(false);

  // Recovery state
  const lastProgressTsRef = useRef(Date.now());
  const recoveryAttemptsRef = useRef(0);
  const recoveryStatusRef = useRef(null); // null | 'attempting' | 'gave-up'

  // Spinner debounce
  const waitingTimerRef = useRef(null);

  // Decode-quality watchdog
  const lastQualityRef = useRef({ total: 0, dropped: 0 });

  // Smart autoplay
  const autoplayAttemptedRef = useRef(false);
  const hasStartedRef = useRef(false);

  // Progress emit
  const progressTimerRef = useRef(null);

  // Imperative handle for parent — match ReactPlayer's contract.
  useImperativeHandle(ref, () => ({
    seekTo: (amount) => {
      const v = videoRef.current;
      if (!v) return;
      // ReactPlayer treats 0..1 values as fraction-of-duration.
      if (amount >= 0 && amount <= 1) {
        v.currentTime = amount * (v.duration || 0);
      } else {
        v.currentTime = amount;
      }
    },
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    getDuration: () => videoRef.current?.duration || 0,
    getInternalPlayer: () => videoRef.current,
    // Parity with the legacy `playerRef.current.player.handleReady()` call
    // in index.jsx (used in the "play again" reset path).
    player: { handleReady: () => { /* no-op — native <video> reloads via src reset */ } },
  }));

  // ----- preload strategy -----
  const preloadStrategy = (() => {
    const conn = typeof navigator !== 'undefined'
      && (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
    if (conn?.downlink && conn.downlink < 1) return 'metadata';
    if (probe?.sizeBytes && probe.sizeBytes > 500 * 1024 * 1024) return 'metadata';
    return 'auto';
  })();

  // ----- type-identification log -----
  // Runs whenever url/probe changes. Reports which format the classifier
  // identified, which pipeline will handle it, and the probe summary.
  useEffect(() => {
    if (!url) return;
    const cls = classifyFormat(url, probe);
    let pipeline;
    if (tsMode) pipeline = 'mpegts.js (MPEG-TS transmux)';
    else if (cls.kind === 'unsupported') pipeline = 'none — unsupported format';
    else pipeline = 'native <video src> (mp4box video-only on decode error)';
    // eslint-disable-next-line no-console
    console.log(
      `🎞️ [NativeVideoPlayer] identified ${cls.kind.toUpperCase()} → ${pipeline}`,
      {
        url,
        classification: cls,
        tsMode,
        preloadStrategy,
        probe: probe ? {
          available: probe.available,
          contentType: probe.contentType,
          sizeBytes: probe.sizeBytes,
          sizeMB: probe.sizeBytes ? +(probe.sizeBytes / 1048576).toFixed(2) : null,
          acceptRanges: probe.acceptRanges,
          faststart: probe.faststart,
          error: probe.error,
        } : null,
      },
    );
  }, [url, probe, tsMode, preloadStrategy]);

  // ----- buffer-ahead recalc, playhead-aware -----
  const recalcLoaded = useCallback(() => {
    const v = videoRef.current;
    if (!v) return { loaded: 0, loadedSeconds: 0 };
    const total = v.duration;
    const ranges = v.buffered;
    if (!total || !Number.isFinite(total) || total <= 0 || !ranges || ranges.length === 0) {
      return { loaded: 0, loadedSeconds: 0 };
    }
    const t = v.currentTime;
    let bufferedEnd = 0;
    for (let i = 0; i < ranges.length; i += 1) {
      const start = ranges.start(i);
      const end = ranges.end(i);
      if (t >= start && t <= end) {
        bufferedEnd = end;
        break;
      }
      if (end > bufferedEnd) bufferedEnd = end;
    }
    return {
      loaded: Math.min(1, Math.max(0, bufferedEnd / total)),
      loadedSeconds: bufferedEnd,
    };
  }, []);

  // ----- recovery (declared up here so deps work) -----
  const attemptRecover = useCallback((cause = 'stall') => {
    const v = videoRef.current;
    if (!v) return;
    if (fallbackState !== 'idle') return; // MSE/mpegts pipelines own their own
    if (recoveryAttemptsRef.current >= 3) {
      recoveryStatusRef.current = 'gave-up';
      if (onError) onError(new Error('Connection lost — playback stalled after 3 recovery attempts.'));
      return;
    }
    recoveryAttemptsRef.current += 1;
    recoveryStatusRef.current = 'attempting';
    lastProgressTsRef.current = Date.now();

    const t = v.currentTime;
    const wasPlaying = !v.paused && !v.ended;

    if (recoveryAttemptsRef.current === 1 && cause !== 'network-error') {
      // Soft nudge: 0.5 s backwards re-issues the byte-range request
      // for the current segment without dropping decoder state.
      v.currentTime = Math.max(0, t - 0.5);
      if (wasPlaying) v.play().catch(() => {});
    } else {
      // Hard reload. Restore position on `loadeddata` (NOT loadedmetadata —
      // duration isn't finite yet at that point on some browsers).
      const restore = () => {
        try { v.currentTime = t; } catch { /* ignore */ }
        if (wasPlaying) v.play().catch(() => {});
        v.removeEventListener('loadeddata', restore);
      };
      v.addEventListener('loadeddata', restore);
      v.load();
    }
  }, [fallbackState, onError]);

  // ----- <video> event wiring -----
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const handleLoadedMetadata = () => {
      if (onReady) onReady();
    };

    const handleCanPlay = () => {
      recoveryAttemptsRef.current = 0;
      recoveryStatusRef.current = null;
      if (onBufferEnd) onBufferEnd();

      // Smart autoplay: try unmuted, fall back to muted on rejection.
      if (autoPlay && v.paused && !autoplayAttemptedRef.current) {
        autoplayAttemptedRef.current = true;
        const wasMuted = v.muted;
        const p = v.play();
        if (p && p.catch) {
          p.catch((err) => {
            if (err?.name === 'NotAllowedError' && !wasMuted) {
              v.muted = true;
              v.play().catch(() => {
                if (onError) onError(err);
              });
            } else if (onError) {
              onError(err);
            }
          });
        }
      }
    };

    const handleProgressEvt = () => {
      lastProgressTsRef.current = Date.now();
    };

    const handleWaiting = () => {
      if (waitingTimerRef.current) clearTimeout(waitingTimerRef.current);
      // Debounce: < 400 ms stalls don't flash the spinner.
      waitingTimerRef.current = setTimeout(() => {
        if (onBuffer) onBuffer();
        waitingTimerRef.current = null;
      }, 400);
    };

    const handlePlaying = () => {
      if (waitingTimerRef.current) {
        clearTimeout(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        if (onStart) onStart();
      }
      if (onPlay) onPlay();
    };

    const handleSeeking = () => {
      // Seeking truly stalls until new region buffers — surface immediately.
      if (onBuffer) onBuffer();
    };

    const handleEnded = () => { if (onEnded) onEnded(); };

    const handleVideoError = (e) => {
      const code = v.error ? v.error.code : 0;

      // In TS / MSE-fallback modes those pipelines own error reporting.
      if (tsMode) return;
      if (fallbackState !== 'idle' && fallbackState !== 'failed') return;

      // Code 3: decode error on an MP4-shaped source → try video-only.
      if (
        code === 3
        && !fallbackAttemptedRef.current
        && url
        && looksLikeMp4(url, probe)
      ) {
        fallbackAttemptedRef.current = true;
        setFallbackState('recovering');
        // eslint-disable-next-line no-console
        console.log('🛠️ [NativeVideoPlayer] decode error (code 3) on MP4 → switching to mp4box video-only pipeline', { url });
        const ctrl = new AbortController();
        fallbackAbortRef.current = ctrl;
        mseVideoOnly(v, url, { signal: ctrl.signal })
          .then(() => {
            setFallbackState('active');
            // eslint-disable-next-line no-console
            console.log('✅ [NativeVideoPlayer] mp4box video-only pipeline active (audio dropped)');
            v.play().catch(() => {});
          })
          .catch((err) => {
            if (err?.name === 'AbortError') return;
            setFallbackState('failed');
            // eslint-disable-next-line no-console
            console.log('❌ [NativeVideoPlayer] mp4box video-only pipeline failed', err);
            if (onError) onError(new Error('Decode error — video-only recovery also failed.'));
          });
        return;
      }

      // Code 2: network — let watchdog recover (up to 3 attempts).
      if (code === 2 && fallbackState === 'idle' && recoveryAttemptsRef.current < 3) {
        attemptRecover('network-error');
        return;
      }

      // Anything else → surface as a real error.
      const map = {
        1: 'Playback aborted.',
        2: 'Network error — check your connection.',
        3: 'Decode error — file is malformed at the source.',
        4: 'This format isn\'t supported. Re-encode the source as H.264 + AAC MP4.',
      };
      if (onError) onError(new Error(map[code] || `Playback error (code ${code}).`));
    };

    const handleStalled = () => {
      if (waitingTimerRef.current) clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = setTimeout(() => {
        if (onBuffer) onBuffer();
        waitingTimerRef.current = null;
      }, 400);
    };

    v.addEventListener('loadedmetadata', handleLoadedMetadata);
    v.addEventListener('canplay', handleCanPlay);
    v.addEventListener('progress', handleProgressEvt);
    v.addEventListener('waiting', handleWaiting);
    v.addEventListener('playing', handlePlaying);
    v.addEventListener('seeking', handleSeeking);
    v.addEventListener('ended', handleEnded);
    v.addEventListener('error', handleVideoError);
    v.addEventListener('stalled', handleStalled);

    return () => {
      v.removeEventListener('loadedmetadata', handleLoadedMetadata);
      v.removeEventListener('canplay', handleCanPlay);
      v.removeEventListener('progress', handleProgressEvt);
      v.removeEventListener('waiting', handleWaiting);
      v.removeEventListener('playing', handlePlaying);
      v.removeEventListener('seeking', handleSeeking);
      v.removeEventListener('ended', handleEnded);
      v.removeEventListener('error', handleVideoError);
      v.removeEventListener('stalled', handleStalled);
    };
    // url / probe in deps so handleVideoError closes over the latest values.
  }, [url, probe, tsMode, fallbackState, autoPlay, attemptRecover, onReady, onPlay, onStart, onBuffer, onBufferEnd, onEnded, onError]);

  // ----- progress emit (ReactPlayer-shaped payload) -----
  useEffect(() => {
    if (!onProgress) return undefined;
    const id = setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.duration || !Number.isFinite(v.duration)) return;
      const { loaded, loadedSeconds } = recalcLoaded();
      onProgress({
        played: v.duration > 0 ? v.currentTime / v.duration : 0,
        playedSeconds: v.currentTime,
        loaded,
        loadedSeconds,
      });
    }, progressInterval);
    progressTimerRef.current = id;
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [onProgress, progressInterval, recalcLoaded]);

  // ----- stall watchdog -----
  useEffect(() => {
    const id = setInterval(() => {
      const v = videoRef.current;
      if (!v || v.paused || v.ended) return;
      if (fallbackState !== 'idle') return;
      if (recoveryStatusRef.current === 'attempting') return;
      if (v.readyState >= 3) return; // HAVE_FUTURE_DATA — fine
      if (Date.now() - lastProgressTsRef.current > 15000) {
        attemptRecover('stall');
      }
    }, 3000);
    return () => clearInterval(id);
  }, [fallbackState, attemptRecover]);

  // ----- online listener — kick a recovery the moment connectivity is back -----
  useEffect(() => {
    const onOnline = () => {
      const v = videoRef.current;
      if (!v) return;
      if (fallbackState !== 'idle') return;
      if (!v.paused && v.readyState < 3) attemptRecover('network-back');
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [fallbackState, attemptRecover]);

  // ----- decode-quality watchdog -----
  useEffect(() => {
    if (!onDecodeWarning) return undefined;
    const id = setInterval(() => {
      const v = videoRef.current;
      if (!v || v.paused || v.ended) return;
      if (typeof v.getVideoPlaybackQuality !== 'function') return;
      const q = v.getVideoPlaybackQuality();
      const last = lastQualityRef.current;
      const dt = q.totalVideoFrames - last.total;
      const dd = q.droppedVideoFrames - last.dropped;
      lastQualityRef.current = {
        total: q.totalVideoFrames,
        dropped: q.droppedVideoFrames,
      };
      if (dt < 30) return; // need a meaningful sample window
      const dropRate = dd / dt;
      if (dropRate > 0.05) {
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onDecodeWarning({
          dropRate,
          dimensions: w && h ? { width: w, height: h } : null,
        });
      }
    }, 3000);
    return () => clearInterval(id);
  }, [onDecodeWarning]);

  // ----- reset on URL change -----
  useEffect(() => {
    hasStartedRef.current = false;
    autoplayAttemptedRef.current = false;
    recoveryAttemptsRef.current = 0;
    recoveryStatusRef.current = null;
    lastProgressTsRef.current = Date.now();
    fallbackAttemptedRef.current = false;
    setFallbackState('idle');
    if (fallbackAbortRef.current) {
      fallbackAbortRef.current.abort();
      fallbackAbortRef.current = null;
    }
    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
    lastQualityRef.current = { total: 0, dropped: 0 };
  }, [url]);

  // ----- unsupported-format short-circuit -----
  useEffect(() => {
    if (!url) return;
    if (tsMode) return;
    const cls = classifyFormat(url, probe);
    if (cls.kind === 'unsupported' && onError) {
      onError(new Error("This format isn't supported in-browser. Convert the source to H.264 + AAC MP4."));
    }
  }, [url, probe, tsMode, onError]);

  // ----- MPEG-TS attach -----
  useEffect(() => {
    if (!tsMode || !url) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    const ctrl = new AbortController();
    let cancelled = false;

    attachMpegts(v, url, { signal: ctrl.signal })
      .then(({ player }) => {
        if (cancelled) return;
        tsPlayerRef.current = player;
        // eslint-disable-next-line no-console
        console.log('✅ [NativeVideoPlayer] mpegts.js attached — MPEG-TS transmuxing to MSE', { url });
      })
      .catch((e) => {
        if (cancelled || e?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.log('❌ [NativeVideoPlayer] mpegts.js attach failed', e);
        if (onError) onError(new Error(`Couldn't play MPEG-TS: ${e.message || String(e)}`));
      });

    return () => {
      cancelled = true;
      ctrl.abort();
      const p = tsPlayerRef.current;
      if (p) {
        try { p.unload(); } catch { /* ignore */ }
        try { p.detachMediaElement(); } catch { /* ignore */ }
        try { p.destroy(); } catch { /* ignore */ }
        tsPlayerRef.current = null;
      }
    };
  }, [tsMode, url, onError]);

  // ----- play/pause / volume / mute / rate sync with props -----
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.play().catch(() => { /* may be autoplay-blocked — handled in canplay */ });
    } else {
      v.pause();
    }
  }, [playing]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.volume = volume;
  }, [volume]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = playbackRate;
  }, [playbackRate]);

  // Cancel in-flight async on unmount.
  useEffect(() => () => {
    if (fallbackAbortRef.current) fallbackAbortRef.current.abort();
  }, []);

  // TS and MSE-fallback pipelines bind a MediaSource — don't fight them
  // with a src attribute on the element.
  const srcProp = (tsMode || fallbackState === 'active' || fallbackState === 'recovering')
    ? {}
    : { src: url || undefined };

  return (
    <video
      ref={videoRef}
      width={width}
      height={height}
      className={className}
      style={style}
      preload={preloadStrategy}
      playsInline
      // eslint-disable-next-line react/no-unknown-property
      x-webkit-airplay="allow"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...srcProp}
    />
  );
});

NativeVideoPlayer.displayName = 'NativeVideoPlayer';

export default NativeVideoPlayer;
